import {
  DEFAULT_AUTOSAVE_TRIGGERS,
  type AutosaveTriggerName,
  type CheckpointMeta,
  type ComposeNotice,
  type GameVersion,
  type OfflineRejectReason,
  type SessionAdoptRequest,
  type SessionStartResponse,
  type SseEvent,
} from "@redchain/contracts";
import { fromHex, sha256, toHex, zstdCompress, zstdDecompress } from "@redchain/input-log";
import { ApiRequestError, NetworkError, type ApiClient } from "../api/client";
import { sseBus } from "../api/events";
import { env } from "../env";
import { Backoff } from "./backoff";
import {
  deletePendingCheckpoint,
  deletePendingChunk,
  dropSessionPending,
  getCanonicalSram,
  getSession,
  pendingForSession,
  putCanonicalSram,
  putPendingCheckpoint,
  putPendingChunk,
  putSession,
  sessionsWithPending,
  type PendingCheckpoint,
  type PendingChunk,
  type SessionRecord,
} from "./db";
import { lastRomPatch } from "./romPatch";
import { SseReader } from "./sseReader";

export interface StartedSession {
  sessionId: string;
  sram: Uint8Array;
  sramSha256: Uint8Array;
  serverNonce: Uint8Array;
  gameVersion: GameVersion | null;
  romPatch: SessionStartResponse["romPatch"];
  autosaveTriggers: AutosaveTriggerName[];
  overflow: SessionStartResponse["overflow"];
  notices: ComposeNotice[];
  offline: boolean;
}

export interface QueueStatus {
  pendingChunks: number;
  pendingCheckpoints: number;
  online: boolean;
}

/** What happened to an offline session on reconnect; `live` if it is the session being played now. */
export type OfflineResult = { kind: "adopted"; live: boolean } | { kind: "discarded"; reason: OfflineRejectReason; live: boolean };

type AdoptOutcome = { kind: "adopted" } | { kind: "rejected"; reason: OfflineRejectReason } | { kind: "offline" } | { kind: "retry" };

type QueueItem = { kind: "chunk"; item: PendingChunk } | { kind: "checkpoint"; item: PendingCheckpoint };

const HEARTBEAT_MS = 60_000;
/** fetch keepalive bodies share a ~64 KiB budget per page. */
const KEEPALIVE_BUDGET = 60 * 1024;
const DRAIN_TIMEOUT_MS = 20_000;
const ADOPT_RETRY_MS = 30_000;

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

let latestGameCheckpoint: { player: string; checkpointId: string } | null = null;

/** Server id of the newest game-save checkpoint uploaded in this tab for `player` (Cable Club lobby binding). */
export function lastGameCheckpointFor(player: string): string | null {
  return latestGameCheckpoint?.player === player ? latestGameCheckpoint.checkpointId : null;
}

export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "").toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(hex)) throw new Error(`not a UUID: ${uuid}`);
  return fromHex(hex);
}

/**
 * Session start, write-before-upload of chunks and checkpoints (IndexedDB first), ordered
 * uploads with 1 s → 60 s jittered backoff, keepalive flush on pagehide, startup drain of
 * older sessions, superseded handling, SSE with Last-Event-ID, offline boot and adoption of
 * offline sessions on reconnect (§7.5, WEB-11).
 */
/**
 * Early decision for a refused upload. Null leaves the caller's 409 sequence check and the
 * generic 4xx drop in place. A poisoned session stays queued so a restore can accept it.
 */
export function refusedUpload(status: number, reason: string | undefined): "superseded" | "retry" | null {
  if (reason === "superseded") return "superseded";
  if (reason === "session_poisoned") return "retry";
  if (status === 429 || status >= 500 || status === 401) return "retry";
  return null;
}

export class SessionClient {
  sessionId: string | null = null;
  /** Session the server doesn't know yet (API unreachable at start): recorded to IndexedDB until it is adopted. */
  offline = false;
  private offlineRecord: SessionRecord | null = null;
  /** The server refused this offline session: nothing more is recorded. */
  private discarded = false;
  private adopting = false;
  private adoptTimer: ReturnType<typeof setInterval> | null = null;
  /** Server id of the newest uploaded game-save checkpoint (the Cable Club save binds to a link lobby). */
  lastGameCheckpointId: string | null = null;
  private queue: QueueItem[] = [];
  private pumping = false;
  private stopped = false;
  private online = true;
  private readonly backoff = new Backoff();
  private wake: (() => void) | null = null;
  private sse: SseReader | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private readonly sramByCheckpointId = new Map<string, Uint8Array>();
  private lastVerifiedSram: Uint8Array | null = null;
  /** Server ids of this session's uploaded checkpoints by frame, and waiters for ones still queued. */
  private readonly checkpointIdByFrame = new Map<string, string>();
  private readonly checkpointWaiters = new Map<string, ((id: string) => void)[]>();

  onQueue: (s: QueueStatus) => void = () => {};
  onSuperseded: () => void = () => {};
  onCheckpointUploaded: (meta: CheckpointMeta, checkpointId: string) => void = () => {};
  onEvent: (ev: SseEvent) => void = () => {};
  onSseStatus: (connected: boolean) => void = () => {};
  onOfflineResult: (r: OfflineResult) => void = () => {};

  constructor(
    private readonly api: ApiClient,
    private readonly player: string,
  ) {}

  // ---------------------------------------------------------------- startup drain

  /**
   * Uploads pending data left by earlier sessions before a new one starts (§7.5). An offline
   * session is adopted first; if the server refuses it, its data is deleted.
   * Returns "offline" if the API is unreachable.
   */
  async drainPending(onProgress?: (remaining: number) => void): Promise<"done" | "offline"> {
    const deadline = Date.now() + DRAIN_TIMEOUT_MS;
    const sessions = await sessionsWithPending();
    for (const sessionId of sessions) {
      const record = await getSession(sessionId).catch(() => undefined);
      const backoff = new Backoff();
      if (record?.offline && !record.adopted) {
        const outcome = await this.adoptUntilSettled(record, deadline, backoff);
        if (outcome === "offline") return "offline";
        if (outcome === "discarded") continue;
        backoff.reset();
      }
      const { chunks, checkpoints } = await pendingForSession(sessionId);
      const items = SessionClient.order(chunks, checkpoints);
      while (items.length > 0) {
        onProgress?.(items.length);
        const head = items[0]!;
        const result = await this.uploadOne(sessionId, head, false);
        if (result === "ok" || result === "drop") {
          items.shift();
          continue;
        }
        if (result === "superseded") {
          await dropSessionPending(sessionId);
          break;
        }
        if (result === "offline" && (Date.now() > deadline || !navigator.onLine)) return "offline";
        if (Date.now() > deadline) throw new Error("Couldn't upload your last session yet. Please try again in a moment.");
        await backoff.wait();
      }
      // An adopted offline session is over once its data is in; nothing else will end it.
      if (record?.offline && items.length === 0) await this.api.endSession(sessionId).catch(() => {});
    }
    onProgress?.(0);
    return "done";
  }

  private async adoptUntilSettled(record: SessionRecord, deadline: number, backoff: Backoff): Promise<"adopted" | "discarded" | "offline"> {
    for (;;) {
      const outcome = await this.adopt(record);
      if (outcome.kind === "adopted") {
        this.onOfflineResult({ kind: "adopted", live: false });
        return "adopted";
      }
      if (outcome.kind === "rejected") {
        await dropSessionPending(record.sessionId);
        this.onOfflineResult({ kind: "discarded", reason: outcome.reason, live: false });
        return "discarded";
      }
      if (outcome.kind === "offline" && (Date.now() > deadline || !navigator.onLine)) return "offline";
      if (Date.now() > deadline) throw new Error("Couldn't upload your last session yet. Please try again in a moment.");
      await backoff.wait();
    }
  }

  /** POST /sessions/adopt for an offline session (§7.5). Records the adoption locally. */
  private async adopt(record: SessionRecord): Promise<AdoptOutcome> {
    // Offline sessions recorded before adoption existed carry no base to check against.
    if (!record.adopt) return { kind: "rejected", reason: "rules_changed" };
    try {
      const res = await this.api.adoptSession(record.adopt);
      if (res.status === "rejected") return { kind: "rejected", reason: res.reason };
      await putSession({ ...record, adopted: true }).catch((e: unknown) => console.warn("Couldn't record the adoption locally", e));
      return { kind: "adopted" };
    } catch (e) {
      if (e instanceof NetworkError) return { kind: "offline" };
      if (!(e instanceof ApiRequestError)) return { kind: "retry" };
      if (e.status === 401 || e.status === 429 || e.status >= 500 || e.body.reason === "previous_save_pending") return { kind: "retry" };
      console.error("Offline session refused", e.body);
      return { kind: "rejected", reason: "rules_changed" };
    }
  }

  // ---------------------------------------------------------------- session start

  async start(args: { romSha1: string; coreBuildHash: string }): Promise<StartedSession> {
    let res: SessionStartResponse;
    try {
      res = await this.api.startSession({ coreBuildHash: args.coreBuildHash, romSha1: args.romSha1, clientVersion: env.clientVersion });
    } catch (e) {
      if (e instanceof NetworkError) return this.startOffline(args);
      throw e;
    }
    const sram = await zstdDecompress(base64ToBytes(res.sram));
    const sha = sha256(sram);
    if (toHex(sha) !== res.sramSha256) throw new Error("Composed save failed its integrity check. Please reload.");

    this.sessionId = res.sessionId;
    this.offline = false;
    await putSession({ sessionId: res.sessionId, player: this.player, startedAt: Date.now(), offline: false, superseded: false });
    await putCanonicalSram({ player: this.player, sram, sha256: res.sramSha256, savedAt: Date.now(), source: "session_start", saveSeq: res.baseSaveSeq });
    this.begin();
    return {
      sessionId: res.sessionId,
      sram,
      sramSha256: sha,
      serverNonce: fromHex(res.serverNonce),
      gameVersion: res.gameVersion,
      romPatch: res.romPatch,
      autosaveTriggers: res.autosaveTriggers,
      overflow: res.overflow,
      notices: res.notices,
      offline: false,
    };
  }

  /**
   * Boots from the last canonical SRAM held locally when the API is unreachable (§7.5). The
   * session id and nonce are local; the PKIL header carries them, and the server records them
   * when it adopts the session on reconnect (here, or at the next page load's drain). Until then
   * nothing uploads: no SSE, heartbeat or upload pump runs.
   */
  private async startOffline(args: { romSha1: string; coreBuildHash: string }): Promise<StartedSession> {
    const canonical = await getCanonicalSram(this.player).catch(() => undefined);
    if (!canonical) throw new NetworkError(new Error("You're offline and there's no saved game on this device yet."));
    const sessionId = crypto.randomUUID();
    const nonce = crypto.getRandomValues(new Uint8Array(32));
    const romPatch = lastRomPatch();
    const startedAt = Date.now();
    const adopt: SessionAdoptRequest = {
      sessionId,
      nonce: toHex(nonce),
      startedAt: new Date(startedAt).toISOString(),
      baseSramSha256: canonical.sha256,
      baseSaveSeq: canonical.source === "session_start" ? (canonical.saveSeq ?? null) : null,
      coreBuildHash: args.coreBuildHash,
      romSha1: args.romSha1,
      clientVersion: env.clientVersion,
      romPatch,
    };
    this.sessionId = sessionId;
    this.online = false;
    this.offline = true;
    this.offlineRecord = { sessionId, player: this.player, startedAt, offline: true, superseded: false, adopt };
    await putSession(this.offlineRecord).catch((e: unknown) => console.warn("Couldn't record the offline session locally", e));
    this.begin();
    return {
      sessionId,
      sram: canonical.sram,
      sramSha256: fromHex(canonical.sha256),
      serverNonce: nonce,
      gameVersion: null,
      romPatch,
      autosaveTriggers: DEFAULT_AUTOSAVE_TRIGGERS,
      overflow: [],
      notices: [],
      offline: true,
    };
  }

  private begin(): void {
    this.stopped = false;
    if (this.offline) {
      window.addEventListener("online", this.tryAdopt);
      this.adoptTimer = setInterval(this.tryAdopt, ADOPT_RETRY_MS);
      this.emitQueue();
      return;
    }
    this.goLive();
  }

  /** Reconnect during offline play: adopt the running session, then upload as if it had started online. */
  private tryAdopt = async (): Promise<void> => {
    const record = this.offlineRecord;
    if (!this.offline || this.stopped || this.discarded || this.adopting || !record) return;
    this.adopting = true;
    try {
      const outcome = await this.adopt(record);
      if (this.stopped || this.sessionId !== record.sessionId) return;
      if (outcome.kind === "adopted") {
        this.stopAdoptRetry();
        this.offline = false;
        this.online = true;
        this.goLive();
        this.emitQueue();
        this.onOfflineResult({ kind: "adopted", live: true });
      } else if (outcome.kind === "rejected") {
        this.stopAdoptRetry();
        await this.serial(async () => {
          this.discarded = true;
          this.queue = [];
          await dropSessionPending(record.sessionId);
        });
        this.emitQueue();
        this.onOfflineResult({ kind: "discarded", reason: outcome.reason, live: true });
      }
    } finally {
      this.adopting = false;
    }
  };

  private stopAdoptRetry(): void {
    if (this.adoptTimer) clearInterval(this.adoptTimer);
    this.adoptTimer = null;
    window.removeEventListener("online", this.tryAdopt);
  }

  private goLive(): void {
    window.addEventListener("pagehide", this.onPageHide);
    window.addEventListener("online", this.kick);
    this.heartbeat = setInterval(() => {
      if (this.sessionId && this.online) void this.api.heartbeat(this.sessionId).catch(() => {});
    }, HEARTBEAT_MS);
    this.openEvents();
    void this.pump();
  }

  // ---------------------------------------------------------------- worker output

  /** Worker output is persisted strictly in arrival order, so a checkpoint never overtakes its chunk. */
  private enqueueChain: Promise<void> = Promise.resolve();

  private serial(fn: () => Promise<void>): Promise<void> {
    this.enqueueChain = this.enqueueChain.then(fn).catch((e) => console.error("Failed to persist upload", e));
    return this.enqueueChain;
  }

  enqueueChunk(c: { seq: number; bytes: Uint8Array; chainHash: string; firstFrame: bigint; lastFrame: bigint }): Promise<void> {
    return this.serial(async () => {
      if (!this.sessionId || this.discarded) return;
      const item: PendingChunk = {
        sessionId: this.sessionId,
        seq: c.seq,
        bytes: await zstdCompress(c.bytes),
        chainHash: c.chainHash,
        firstFrame: c.firstFrame.toString(),
        lastFrame: c.lastFrame.toString(),
        createdAt: Date.now(),
      };
      await putPendingChunk(item);
      this.insert({ kind: "chunk", item });
    });
  }

  enqueueCheckpoint(meta: CheckpointMeta, sram: Uint8Array): Promise<void> {
    return this.serial(async () => {
      if (!this.sessionId || this.discarded) return;
      const item: PendingCheckpoint = {
        sessionId: this.sessionId,
        seqInSession: meta.seqInSession,
        meta,
        sramZst: await zstdCompress(sram),
        sram,
        createdAt: Date.now(),
      };
      await putPendingCheckpoint(item);
      this.insert({ kind: "checkpoint", item });
    });
  }

  private insert(q: QueueItem): void {
    this.queue.push(q);
    this.queue.sort(SessionClient.compare);
    this.emitQueue();
    this.kick();
  }

  private static compare(a: QueueItem, b: QueueItem): number {
    const ka = a.kind === "chunk" ? a.item.seq : a.item.meta.lastChunkSeq;
    const kb = b.kind === "chunk" ? b.item.seq : b.item.meta.lastChunkSeq;
    if (ka !== kb) return ka - kb;
    if (a.kind !== b.kind) return a.kind === "chunk" ? -1 : 1;
    return a.kind === "checkpoint" && b.kind === "checkpoint" ? a.item.seqInSession - b.item.seqInSession : 0;
  }

  private static order(chunks: PendingChunk[], checkpoints: PendingCheckpoint[]): QueueItem[] {
    const items: QueueItem[] = [
      ...chunks.map((item) => ({ kind: "chunk" as const, item })),
      ...checkpoints.map((item) => ({ kind: "checkpoint" as const, item })),
    ];
    return items.sort(SessionClient.compare);
  }

  private emitQueue(): void {
    this.onQueue({
      pendingChunks: this.queue.filter((q) => q.kind === "chunk").length,
      pendingCheckpoints: this.queue.filter((q) => q.kind === "checkpoint").length,
      online: this.online,
    });
  }

  private kick = (): void => {
    this.backoff.reset();
    this.wake?.();
    void this.pump();
  };

  // ---------------------------------------------------------------- upload pump

  private async pump(): Promise<void> {
    if (this.pumping || this.stopped || this.offline || !this.sessionId) return;
    this.pumping = true;
    try {
      while (!this.stopped && this.queue.length > 0 && this.sessionId) {
        const head = this.queue[0]!;
        const result = await this.uploadOne(this.sessionId, head, false);
        if (result === "ok" || result === "drop") {
          this.queue.shift();
          this.backoff.reset();
          this.setOnline(true);
          this.emitQueue();
          continue;
        }
        if (result === "superseded") {
          this.handleSuperseded();
          return;
        }
        this.setOnline(result !== "offline");
        await new Promise<void>((resolve) => {
          const t = setTimeout(resolve, this.backoff.nextDelay());
          this.wake = () => {
            clearTimeout(t);
            resolve();
          };
        });
        this.wake = null;
      }
    } finally {
      this.pumping = false;
    }
  }

  private setOnline(online: boolean): void {
    if (online === this.online) return;
    this.online = online;
    this.emitQueue();
  }

  /** Uploads one item and removes it from IndexedDB on success. */
  private async uploadOne(sessionId: string, q: QueueItem, keepalive: boolean): Promise<"ok" | "drop" | "retry" | "offline" | "superseded"> {
    try {
      if (q.kind === "chunk") {
        await this.api.uploadChunk(sessionId, q.item, keepalive);
        await deletePendingChunk(sessionId, q.item.seq);
      } else {
        const { checkpointId } = await this.api.uploadCheckpoint(sessionId, q.item.meta, q.item.sramZst, keepalive);
        await deletePendingCheckpoint(sessionId, q.item.seqInSession);
        if (sessionId === this.sessionId) {
          this.sramByCheckpointId.set(checkpointId, q.item.sram);
          if (q.item.meta.kind === "game") {
            this.lastGameCheckpointId = checkpointId;
            latestGameCheckpoint = { player: this.player, checkpointId };
          }
          this.checkpointUploaded(q.item.meta.frame, checkpointId);
          this.onCheckpointUploaded(q.item.meta, checkpointId);
        }
      }
      return "ok";
    } catch (e) {
      if (e instanceof NetworkError) return "offline";
      if (!(e instanceof ApiRequestError)) return "retry";
      const refused = refusedUpload(e.status, e.body.reason);
      if (refused) return refused;
      if (q.kind === "chunk" && e.status === 409 && e.body.expectedSeq !== undefined) {
        if (e.body.expectedSeq > q.item.seq) {
          await deletePendingChunk(sessionId, q.item.seq);
          return "drop";
        }
        console.error(`Server expects chunk ${e.body.expectedSeq}, next local chunk is ${q.item.seq}`);
        return "retry";
      }
      if (e.status === 429 || e.status >= 500 || e.status === 401) return "retry";
      // Any other 4xx can never succeed (e.g. kind_mismatch): the server reports it over SSE.
      console.error("Upload refused", e.body);
      if (q.kind === "chunk") await deletePendingChunk(sessionId, q.item.seq);
      else await deletePendingCheckpoint(sessionId, q.item.seqInSession);
      return "drop";
    }
  }

  private checkpointUploaded(frame: string, checkpointId: string): void {
    this.checkpointIdByFrame.set(frame, checkpointId);
    const waiters = this.checkpointWaiters.get(frame);
    this.checkpointWaiters.delete(frame);
    for (const w of waiters ?? []) w(checkpointId);
  }

  /**
   * Server id of this session's checkpoint at `frame`, once it has uploaded (the Cable Club save
   * binds to a link lobby, §17.2 step 2). Rejects after `timeoutMs` or when `signal` aborts.
   */
  checkpointIdAt(frame: bigint, timeoutMs: number, signal?: AbortSignal): Promise<string> {
    const key = frame.toString();
    const known = this.checkpointIdByFrame.get(key);
    if (known) return Promise.resolve(known);
    return new Promise((resolve, reject) => {
      const done = (id: string) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
        resolve(id);
      };
      const drop = () => {
        const list = this.checkpointWaiters.get(key)?.filter((w) => w !== done) ?? [];
        if (list.length) this.checkpointWaiters.set(key, list);
        else this.checkpointWaiters.delete(key);
      };
      const timer = setTimeout(() => {
        drop();
        signal?.removeEventListener("abort", onAbort);
        reject(new Error("Your Cable Club save hasn't uploaded yet. Check your connection."));
      }, timeoutMs);
      const onAbort = () => {
        clearTimeout(timer);
        drop();
        reject(new DOMException("aborted", "AbortError"));
      };
      signal?.addEventListener("abort", onAbort, { once: true });
      this.checkpointWaiters.set(key, [...(this.checkpointWaiters.get(key) ?? []), done]);
    });
  }

  /** On pagehide, send what's left with fetch keepalive (§7.5). IndexedDB keeps it all anyway. */
  private onPageHide = (): void => {
    const sessionId = this.sessionId;
    if (!sessionId || this.stopped) return;
    let budget = KEEPALIVE_BUDGET;
    for (const q of this.queue) {
      const size = q.kind === "chunk" ? q.item.bytes.length : q.item.sramZst.length + 512;
      if (size > budget) break;
      budget -= size;
      if (q.kind === "chunk") void this.api.uploadChunk(sessionId, q.item, true).catch(() => {});
      else void this.api.uploadCheckpoint(sessionId, q.item.meta, q.item.sramZst, true).catch(() => {});
    }
  };

  // ---------------------------------------------------------------- SSE

  private openEvents(): void {
    if (!this.sessionId) return;
    const sessionId = this.sessionId;
    this.sse = new SseReader({
      url: this.api.eventsUrl(sessionId),
      api: this.api,
      lastEventId: sessionStorage.getItem(`redchain.sse.${sessionId}`),
      onStatus: (c) => this.onSseStatus(c),
      onFatal: (status, reason) => {
        if (reason === "superseded") this.handleSuperseded();
        else console.warn("SSE closed", status, reason);
      },
      onEvent: (ev, id) => {
        if (id) sessionStorage.setItem(`redchain.sse.${sessionId}`, id);
        void this.handleEvent(ev);
      },
    });
    this.sse.start();
  }

  private async handleEvent(ev: SseEvent): Promise<void> {
    switch (ev.type) {
      case "session.superseded":
        this.handleSuperseded();
        break;
      case "checkpoint.verified": {
        const sram = this.sramByCheckpointId.get(ev.checkpointId);
        if (sram) this.lastVerifiedSram = sram;
        break;
      }
      case "chain.committed":
        if (this.lastVerifiedSram) {
          const sram = this.lastVerifiedSram;
          await putCanonicalSram({ player: this.player, sram, sha256: toHex(sha256(sram)), savedAt: Date.now(), source: "committed" });
        }
        break;
      default:
        break;
    }
    sseBus.emit(ev);
    this.onEvent(ev);
  }

  private handleSuperseded(): void {
    if (this.stopped) return;
    const sessionId = this.sessionId;
    this.stop();
    if (sessionId) {
      void getSession(sessionId).then((s) => s && putSession({ ...s, superseded: true }));
      void dropSessionPending(sessionId);
    }
    this.onSuperseded();
  }

  // ---------------------------------------------------------------- teardown

  stop(): void {
    this.stopped = true;
    this.stopAdoptRetry();
    this.sse?.stop();
    this.sse = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    window.removeEventListener("pagehide", this.onPageHide);
    window.removeEventListener("online", this.kick);
    this.wake?.();
  }

  /** Explicit exit: flush what we can, then end the session (§10.5). */
  async end(): Promise<void> {
    const sessionId = this.sessionId;
    await Promise.race([this.pump(), new Promise((r) => setTimeout(r, 3_000))]);
    this.stop();
    if (sessionId && this.online && !this.offline) await this.api.endSession(sessionId).catch(() => {});
  }
}
